import { APIConfig } from '../config/APIConfig';
import { buildApiUrl } from '../../config/environment';
import type { ConversionStats } from '../../domain/entities/Analytics';

export interface FilteredStatsParams {
  period: 'today' | 'week' | 'month' | 'custom';
  startDate?: string;
  endDate?: string;
  cameraId?: string;
  cameraIds?: string[];  // Múltiplas câmeras — sobrescreve cameraId
}

export interface FilteredStats {
  period: {
    type: string;
    start: string;
    end: string;
  };
  filter: {
    camera_id: string | null;
    camera_name: string;
  };
  summary: {
    total_detections: number;
    total_people: number;
    exit_people?: number;  // 🚪 Saídas no período (câmeras de saída)
    inside_people?: number;  // 🚪 Pessoas no estabelecimento = entradas - saídas
    average_per_detection: number;
  };
  demographics: {
    gender: {
      male: number;
      female: number;
      male_percentage: number;
      female_percentage: number;
    };
    age_groups: {
      age_8_12: number;
      age_15_20: number;
      age_25_32: number;
      age_adults: number;
      age_60_plus: number;
      age_8_12_percentage: number;
      age_15_20_percentage: number;
      age_25_32_percentage: number;
      age_adults_percentage: number;
      age_60_plus_percentage: number;
    };
  };
  hourly_flow: Array<{
    hour: number;
    detections: number;
    people: number;
    gender?: {
      male: number;
      female: number;
      male_percentage: number;
      female_percentage: number;
    };
    age_groups?: {
      child: number;
      teen: number;
      young_adult: number;
      adult: number;
      elderly: number;
    };
  }>;
  daily_availability?: Array<{
    date: string;
    status: 'consolidated' | 'pending' | 'unavailable' | 'in_progress';
    source_present: boolean;
  }>;
  daily_flow: Array<{
    date: string;       // formato "YYYY-MM-DD"
    detections: number;
    people: number;
  }>;
  by_camera: Array<{
    camera_id: string;
    camera_name: string;
    is_entrance?: boolean;
    is_exit?: boolean;  // 🚪 Câmera de saída (contagem de pessoas)
    detections: number;
    people: number;
    gender?: {
      male: number;
      female: number;
      male_percentage: number;
      female_percentage: number;
    };
    age_groups?: {
      child: number;
      teen: number;
      young_adult: number;
      adult: number;
      elderly: number;
    };
  }>;
  conversion?: ConversionStats | null;
  generated_at: string;
}

export class ReportsService {
  /**
   * Buscar estatísticas com filtros avançados
   */
  static async getFilteredStats(
    params: FilteredStatsParams
  ): Promise<FilteredStats> {
    const queryParams = new URLSearchParams();
    queryParams.append('period', params.period);

    if (params.startDate) {
      queryParams.append('start_date', params.startDate);
    }
    if (params.endDate) {
      queryParams.append('end_date', params.endDate);
    }
    if (params.cameraIds && params.cameraIds.length > 0) {
      if (params.cameraIds.length === 1) {
        queryParams.append('camera_id', params.cameraIds[0]);
      } else {
        queryParams.append('camera_ids', params.cameraIds.join(','));
      }
    } else if (params.cameraId) {
      queryParams.append('camera_id', params.cameraId);
    }

    const response = await APIConfig.getInstance().get<FilteredStats>(
      buildApiUrl(`/api/v1/stats/filtered?${queryParams.toString()}`),
      { timeout: 60000 } // 60 segundos - períodos longos precisam mais tempo
    );

    return response.data;
  }

  /**
   * Exportar relatório em Excel (CSV)
   * 🔒 COM AUTENTICAÇÃO: Usa axios para incluir token no header
   */
  static async exportExcel(params: Partial<FilteredStatsParams>): Promise<void> {
    const queryParams = new URLSearchParams();

    // ✅ CORREÇÃO: Enviar period para API
    if (params.period) {
      queryParams.append('period', params.period);
    }
    if (params.startDate) {
      queryParams.append('start_date', params.startDate);
    }
    if (params.endDate) {
      queryParams.append('end_date', params.endDate);
    }
    if (params.cameraIds && params.cameraIds.length > 0) {
      if (params.cameraIds.length === 1) {
        queryParams.append('camera_id', params.cameraIds[0]);
      } else {
        queryParams.append('camera_ids', params.cameraIds.join(','));
      }
    } else if (params.cameraId) {
      queryParams.append('camera_id', params.cameraId);
    }

    // 🔒 Usar APIConfig para incluir token de autenticação automaticamente
    const response = await APIConfig.getInstance().get(
      buildApiUrl(`/api/v1/reports/export/excel?${queryParams.toString()}`),
      {
        responseType: 'blob', // Importante: receber como blob
        timeout: 90000, // 90 segundos - geração de Excel pode demorar
      }
    );

    // Criar URL do blob e fazer download
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio_shopping_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url); // Limpar memória
  }

  /**
   * Baixar o PDF real do shopping, com os mesmos filtros do dashboard.
   * 🔒 COM AUTENTICAÇÃO: Usa axios para incluir token no header
   */
  static async exportPDF(params: Partial<FilteredStatsParams>): Promise<void> {
    const queryParams = new URLSearchParams();

    // ✅ Incluir period para backend aplicar filtro correto
    if (params.period) {
      queryParams.append('period', params.period);
    }
    if (params.startDate) {
      queryParams.append('start_date', params.startDate);
    }
    if (params.endDate) {
      queryParams.append('end_date', params.endDate);
    }
    if (params.cameraIds && params.cameraIds.length > 0) {
      if (params.cameraIds.length === 1) {
        queryParams.append('camera_id', params.cameraIds[0]);
      } else {
        queryParams.append('camera_ids', params.cameraIds.join(','));
      }
    } else if (params.cameraId) {
      queryParams.append('camera_id', params.cameraId);
    }

    // 🔒 Usar APIConfig para incluir token de autenticação automaticamente
    const response = await APIConfig.getInstance().get(
      buildApiUrl(`/api/v1/reports/export/pdf/download?${queryParams.toString()}`),
      {
        responseType: 'blob',
        timeout: 90000, // 90 segundos - geração de PDF pode demorar
      }
    );

    const blob = response.data as Blob;
    if (blob.type.split(';')[0].toLowerCase() !== 'application/pdf' || blob.size === 0) {
      throw new Error('O servidor não retornou um PDF válido. Tente novamente.');
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const date = new Date().toISOString().split('T')[0];
    link.href = url;
    link.download = `relatorio_shopping_${date}.pdf`;
    document.body.appendChild(link);
    try {
      link.click();
    } finally {
      link.remove();
      // Dar tempo para o navegador iniciar a transferência antes de liberar o blob.
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    }
  }
}
